import { z } from 'zod';
import { firestoreTimestampSchema } from './user'; // Re-use from user.ts
import {
  optionalEmailFieldSchema,
  genericNameFieldSchema, // Keep for portfolio name
  phoneFieldSchema,       // Keep if used elsewhere directly, but use optional for member
  optionalGenericNameFieldSchema, // Import for member names
  optionalPhoneFieldSchema      // Import for member phone
} from '@/utils/validationRules';

// From docs/firestore-schema.md and dump
const portfolioMemberPermissionSchema = z.object({
  name: z.string(),
  checked: z.boolean(),
  id: z.string(),
  description: z.string().optional(),
}).passthrough();
export type PortfolioMemberPermission = z.infer<typeof portfolioMemberPermissionSchema>; // Added export

// 1. Define the core object schema for a member
export const portfolioMemberObjectSchema = z.object({ // Added export here
  userId: z.string(),
  role: z.string(), // e.g., "Owner", "Primary Owner", "Manager"
  status: z.string(), // e.g., "active", "invited"
  permissions: z.array(portfolioMemberPermissionSchema).optional().nullable(),
  email: optionalEmailFieldSchema.nullable(),
  firstName: optionalGenericNameFieldSchema("First name").optional().nullable(),
  lastName: optionalGenericNameFieldSchema("Last name").optional().nullable(),
  phone: optionalPhoneFieldSchema.optional().nullable(),
  avatarUrl: z.string().url().optional().nullable().or(z.literal('')).catch(null), // More permissive
  avatarInitials: z.string().trim().optional().nullable(),
  invitedAt: firestoreTimestampSchema.optional(), // Made optional
  joinedAt: firestoreTimestampSchema.optional(),  // Made optional
}).passthrough(); // Keep passthrough if it was on the original z.object

// 2. Define the exported portfolioMemberSchema using preprocess and the object schema
export const portfolioMemberSchema = z.preprocess((data: any) => {
  if (data && typeof data.id === 'string' && typeof data.userId === 'undefined') {
    return { ...data, userId: data.id };
  }
  return data;
}, portfolioMemberObjectSchema); // Use the defined object schema here

export type PortfolioMember = z.infer<typeof portfolioMemberSchema>; // Keep this export

const billingInfoSchema = z.object({
  address: z.string().trim().optional().nullable(), // Added trim
  paymentMethod: z.string().trim().optional().nullable(), // Added trim
  // Add other billing fields as they become known
}).passthrough().optional().nullable();

export const portfolioSchema = z.object({
  name: genericNameFieldSchema("Portfolio name"),
  type: z.enum(['default', 'agency']).optional().nullable(), // From docs
  
  billingInfo: billingInfoSchema,

  // Owner references from docs/firestore-schema.md
  primaryOwnerRef: z.string().optional().nullable(),
  secondaryOwnerRefs: z.array(z.string()).optional().nullable(),
  managerRefs: z.array(z.string()).optional().nullable(),

  // businessRefs from dump
  businessRefs: z.preprocess((val) => {
    if (typeof val === 'string') {
      try {
        // Attempt to fix common stringified array issues (e.g. single quotes)
        const correctedJsonString = val.replace(/'/g, '"');
        const parsed = JSON.parse(correctedJsonString);
        if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string')) {
          return parsed;
        }
        return val; // Return original if not an array of strings after parsing
      } catch (e) {
        console.warn(`portfolioSchema: Failed to parse businessRefs string: '${val}'. Error: ${e}`);
        return val; // Return original string if parsing fails, Zod will catch type mismatch
      }
    }
    return val;
  }, z.array(z.string()).optional().nullable()),
  
  members: z.preprocess(
    (val) => {
      if (Array.isArray(val)) {
        return val
          .map((memberData) => {
            const result = portfolioMemberSchema.safeParse(memberData);
            if (result.success) {
              return result.data;
            } else {
              console.warn(
                `Portfolio Member Validation Error (ID: ${ (memberData as any)?.id || (memberData as any)?.userId || 'N/A' }):`,
                result.error.issues
              );
              return null; 
            }
          })
          .filter(member => member !== null);
      }
      if (typeof val === 'string') {
        console.warn(
          `portfolioSchema: 'members' field is a string: '${val}'. Attempting to handle, but data is likely corrupt. Will default to empty array.`
        );
        return [];
      }
      return val; 
    },
    z.array(portfolioMemberSchema).optional().nullable()
  ),
  // memberUserIds: z.array(z.string()).optional().nullable(), // Removed as per user directive

  createdAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema, // from docs, might be 'lastUpdated' in dump (string or timestamp)
  lastUpdated: z.union([firestoreTimestampSchema, z.string().datetime().optional()]).optional().nullable(), // Accommodate both possibilities

  // 'owner' field from one sample in dump, might be legacy or specific case.
  // Prioritizing structured refs from docs.
  owner: z.string().optional().nullable(), 

  // 'id' is document ID, will be added to the output schema
}).passthrough();

export type PortfolioData = z.infer<typeof portfolioSchema>; // Represents the data structure within Firestore

// Schema for the portfolio object once it's fetched and has an ID attached
export const portfolioOutputSchema = portfolioSchema.extend({
  id: z.string(),
});
export type Portfolio = z.infer<typeof portfolioOutputSchema>; // This type includes the 'id'
